import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import SvgColor from 'src/components/svg-color';
import { useJobCreationContext } from 'src/sections/job2/hooks/use-job-creation-context';
import { generateCodingQuestion } from 'src/services/role.service';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

export default function GenerateWithAiDialog({ open, setOpen, methods }) {
  // hooks
  const { organization } = useAuthContext();
  const { jobId } = useJobCreationContext();
  const { enqueueSnackbar } = useSnackbar();

  // states
  const [isGeneratingWithAI, setIsGeneratingWithAI] = useState(false);
  const [prompt, setPrompt] = useState('');

  // variables
  const orgId = organization.organization_id;

  // function
  const handleGenerateWithAI = async () => {
    try {
      setIsGeneratingWithAI(true);

      const payload = {
        coding_requirements: prompt,
      };

      const response = await generateCodingQuestion(orgId, jobId, payload);

      if (response.success) {
        // const error = response.body?.error;
        const questionData = response.body?.question_data;
        let questionText = questionData?.question_text;
        const inputOutputList = questionData?.input_output_list;

        if (inputOutputList) {
          inputOutputList.forEach((inputOutput, index) => {
            if (
              inputOutput.input.startsWith('"') &&
              inputOutput.input.endsWith('"')
            ) {
              inputOutputList[index].input = inputOutput.input.slice(1, -1);
            }
            if (
              inputOutput.output.startsWith('"') &&
              inputOutput.output.endsWith('"')
            ) {
              inputOutputList[index].output = inputOutput.output.slice(1, -1);
            }
          });
        }

        if (questionText.startsWith('"') && questionText.endsWith('"')) {
          questionText = questionText.slice(1, -1);
        }
        enqueueSnackbar('Coding question generated successfully');

        methods.setValue('codingAssessments.0.questions.0.text', questionText);
        methods.setValue(
          'codingAssessments.0.questions.0.testCases',
          inputOutputList,
        );
        methods.setValue('codingAssessments.0.questions.0.timeLimit', 30);

        setOpen(false);
      } else throw new Error(response.message);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to generate coding question', {
        variant: 'error',
      });
    } finally {
      setIsGeneratingWithAI(false);
      setPrompt('');
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
      <DialogTitle>Generate with AI</DialogTitle>
      <DialogContent>
        <Stack spacing="16px" width="500px">
          <Stack spacing="12px">
            <Typography variant="subheading1" fontSize={16}>
              Describe what you want to test in the interview
            </Typography>
            <TextField
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setOpen(false);
              }}
              multiline
              rows={12}
              fullWidth
              placeholder='e.g. "Write a function that takes a string as input and returns the number of vowels in the string."'
            />
          </Stack>
          <LoadingButton
            loading={isGeneratingWithAI}
            loadingPosition="start"
            variant="contained"
            sx={{
              width: '150px',
              whiteSpace: 'nowrap',
              color: theme => theme.palette.content.primaryBlack1,
              background: theme => theme.palette.background.primaryGreen3,
              '&:hover': {
                background: theme => theme.palette.background.primaryBlack1,
                color: theme => theme.palette.common.white,
              },
            }}
            startIcon={<SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />}
            onClick={handleGenerateWithAI}
          >
            {isGeneratingWithAI ? 'Generating...' : 'Generate'}
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

GenerateWithAiDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  methods: PropTypes.object,
};
